/* ----------------------------------------------------------------------------
 * Automation-approvals data source (Option a preview panel, owner 2026-07-19).
 *
 * READ-ONLY-FRIENDLY client over the platform's human-owner approval surface:
 *   GET  /v1/approvals?status=            list (read)
 *   GET  /v1/approvals/{id}               get  (read)
 *   POST /v1/approvals/{id}/decide        approve | reject  (HUMAN ROLE_OWNER)
 *   POST /v1/approvals/{id}/cancel        cancel            (HUMAN ROLE_OWNER)
 *
 * INVARIANTS (the UI must NEVER weaken the backend):
 *  - There is deliberately NO create path here. Approvals are prepared ONLY by
 *    platform preparer code; this client can list / get / decide / cancel — never
 *    create, and never any non-allowlisted action.
 *  - The API already gates decisions to ws=1 + ROLE_OWNER + the active pilot
 *    (ApprovalsController::humanOwnerMutation). This client does NOT reimplement
 *    that gate; it renders what the API returns and maps its errors honestly.
 *  - decide/cancel carry a STABLE idempotency key (decisionIdempotencyKey, per
 *    approval + decision) so a deliberate retry de-dupes at the server replay
 *    cache — it can never double-apply a decision.
 *  - Fail-closed: 503 (gateway_unwired / pilot seam inactive), 404, and network
 *    errors resolve to `unavailable` so the panel renders NOTHING (no error
 *    noise). 403 resolves to `restricted` so the panel can show one honest,
 *    non-alarming line.
 *
 * Shared types + the idempotency-key + preview helpers live in the standalone,
 * import-free model (src/voice/actions/approvalActionModel.ts) so they are unit
 * testable without this browser-only client.
 * -------------------------------------------------------------------------- */

import { apiRequest, ApiError, USE_MOCK_DATA } from '@/lib/api';
import type {
  PublicApproval,
  DecisionKind,
  DecisionResult,
} from '@/voice/actions/approvalActionModel';

/** List outcome — never throws for the ordinary "not available / not owner"
 *  cases; the panel switches on `status` and stays quiet when appropriate. */
export type ListApprovalsResult =
  | { status: 'ok'; approvals: PublicApproval[] }
  | { status: 'restricted' } // 403 — non-owner / pilot-restricted
  | { status: 'unavailable' }; // 503 gateway_unwired / 404 / network — render nothing

/** List approvals, optionally filtered by status (defaults to pending only). */
export async function listApprovals(status: string = 'proposed'): Promise<ListApprovalsResult> {
  // Real-data only: the preview panel never renders from mock fixtures. In mock
  // mode there is simply nothing to approve.
  if (USE_MOCK_DATA) return { status: 'ok', approvals: [] };
  try {
    const res = await apiRequest<{ approvals: PublicApproval[] }>('/approvals', {
      method: 'GET',
      query: { status },
    });
    return { status: 'ok', approvals: Array.isArray(res?.approvals) ? res.approvals : [] };
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) return { status: 'restricted' };
    // 503 (gateway_unwired), 404, network (0), or anything else -> stay silent.
    return { status: 'unavailable' };
  }
}

/** Fetch one approval (read). Returns null when unavailable/not found. */
export async function getApproval(id: number): Promise<PublicApproval | null> {
  if (USE_MOCK_DATA) return null;
  try {
    const res = await apiRequest<{ approval: PublicApproval }>(`/approvals/${id}`, { method: 'GET' });
    return res?.approval ?? null;
  } catch {
    return null;
  }
}

/** approve | reject a proposed approval. HUMAN ROLE_OWNER only (server-enforced).
 *  Requires an explicit idempotency key which is sent as X-Bksesh-Idempotency-Key. */
export function decideApproval(
  id: number,
  decision: DecisionKind,
  idempotencyKey: string,
  note?: string,
): Promise<DecisionResult> {
  return apiRequest<DecisionResult>(`/approvals/${id}/decide`, {
    method: 'POST',
    body: note !== undefined ? { decision, note } : { decision },
    idempotencyKey,
  });
}

/** cancel a proposed approval. HUMAN ROLE_OWNER only (server-enforced). */
export function cancelApproval(id: number, idempotencyKey: string): Promise<DecisionResult> {
  return apiRequest<DecisionResult>(`/approvals/${id}/cancel`, {
    method: 'POST',
    body: {},
    idempotencyKey,
  });
}
